import { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import IssueCard from "@/components/IssueCard";
import NewIssueDialog from "@/components/NewIssueDialog";
import CostDialog from "@/components/CostDialog";
import EditIssueDialog from "@/components/EditIssueDialog";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, AlertCircle, Clock, CheckCircle2, Flame, ChevronDown, MapPin, Search, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

function StatCard({ label, value, icon: Icon, tint, testid }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs tracking-[0.2em] uppercase text-gray-500">{label}</p>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${tint}`}>
          <Icon size={16} />
        </div>
      </div>
      <p className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">{value}</p>
    </div>
  );
}

export default function IssuesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [issues, setIssues] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, in_progress: 0, completed: 0, high_priority_open: 0 });
  const [statusTab, setStatusTab] = useState("active");
  const [priority, setPriority] = useState("all");
  const [locationId, setLocationId] = useState("all");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [vendors, setVendors] = useState([]);
  const [costIssue, setCostIssue] = useState(null);
  const [editIssue, setEditIssue] = useState(null);

  const toggleSection = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const load = useCallback(async () => {
    try {
      const params = {};
      // Map tab -> backend status filter. "active" (open+in_progress) is filtered client-side.
      if (statusTab === "open") params.status = "open";
      else if (statusTab === "in_progress") params.status = "in_progress";
      else if (statusTab === "history") params.status = "completed";
      if (priority !== "all") params.priority = priority;
      if (isAdmin && locationId !== "all") params.location_id = locationId;

      const [iRes, lRes, sRes] = await Promise.all([
        api.get("/issues", { params }),
        api.get("/locations"),
        api.get("/stats"),
      ]);
      // Admin-only: load vendors for the cost dialog. Tolerate 403/network failure silently.
      if (isAdmin) {
        api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
      }
      // For "active" tab, exclude completed on the client
      const list = statusTab === "active"
        ? iRes.data.filter((i) => i.status !== "completed")
        : iRes.data;
      setIssues(list);
      setLocations(lRes.data);
      setStats(sRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [statusTab, priority, locationId, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatusChange = async (issue, newStatus) => {
    try {
      await api.patch(`/issues/${issue.id}`, { status: newStatus });
      toast.success(`Marked ${newStatus.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const handleDelete = async (issue) => {
    if (!window.confirm(`Delete "${issue.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/issues/${issue.id}`);
      toast.success("Issue deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const handleClearCompleted = async () => {
    const scope = isAdmin && locationId !== "all"
      ? locations.find((l) => l.id === locationId)?.name || "this location"
      : "all locations";
    if (!window.confirm(`Permanently delete completed issues for ${scope}? This cannot be undone.`)) return;
    try {
      const params = {};
      if (isAdmin && locationId !== "all") params.location_id = locationId;
      const { data } = await api.post("/issues/clear-completed", null, { params });
      toast.success(`${data.deleted} completed issue${data.deleted === 1 ? "" : "s"} cleared`);
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Clear failed");
    }
  };

  // Client-side filter by free-text search
  const filteredIssues = useMemo(() => {
    if (!search.trim()) return issues;
    const q = search.toLowerCase();
    return issues.filter(
      (i) =>
        i.title.toLowerCase().includes(q) ||
        (i.description || "").toLowerCase().includes(q) ||
        (i.location_name || "").toLowerCase().includes(q) ||
        (i.reporter_name || "").toLowerCase().includes(q)
    );
  }, [issues, search]);

  const title = useMemo(
    () => (isAdmin ? "All issues" : "My location issues"),
    [isAdmin]
  );

  const canSubmit = isAdmin ? locations.length > 0 : !!user?.location_id;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">
              {isAdmin ? "Admin control" : "Field view"}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
              {title}
            </h1>
            <p className="text-sm text-gray-600 mt-2">
              {isAdmin
                ? "Review, triage, and complete issues across every location."
                : "Submit new issues and track their resolution."}
            </p>
          </div>
          <Button
            data-testid="open-new-issue"
            className="bg-[#0055FF] hover:bg-[#0044CC] h-11"
            onClick={() => setDialogOpen(true)}
            disabled={!canSubmit}
          >
            <Plus size={16} className="mr-1" /> New issue
          </Button>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard testid="stat-open" label="Open" value={stats.open} icon={AlertCircle} tint="bg-red-50 text-red-600" />
          <StatCard testid="stat-inprogress" label="In progress" value={stats.in_progress} icon={Clock} tint="bg-amber-50 text-amber-600" />
          <StatCard testid="stat-completed" label="Completed" value={stats.completed} icon={CheckCircle2} tint="bg-emerald-50 text-emerald-600" />
          <StatCard testid="stat-high" label="High priority" value={stats.high_priority_open} icon={Flame} tint="bg-gray-900 text-white" />
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Tabs value={statusTab} onValueChange={setStatusTab} data-testid="status-tabs">
              <TabsList>
                <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
                <TabsTrigger value="open" data-testid="tab-open">Open</TabsTrigger>
                <TabsTrigger value="in_progress" data-testid="tab-inprogress">In progress</TabsTrigger>
                <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex flex-wrap items-center gap-2 ml-auto">
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="w-[160px]" data-testid="filter-priority">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priorities</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>

              {isAdmin && (
                <Select value={locationId} onValueChange={setLocationId}>
                  <SelectTrigger className="w-[200px]" data-testid="filter-location">
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All locations</SelectItem>
                    {locations.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title, description, location, reporter…"
                className="pl-9"
              />
            </div>
            {isAdmin && statusTab === "history" && issues.length > 0 && (
              <Button
                data-testid="clear-completed-btn"
                variant="outline"
                onClick={handleClearCompleted}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              >
                <Trash2 size={14} className="mr-1" />
                Clear completed{locationId !== "all" ? " here" : ""}
              </Button>
            )}
          </div>
        </div>

        {/* Issue list */}
        {loading ? (
          <div className="text-center text-sm text-gray-500 py-16">Loading…</div>
        ) : filteredIssues.length === 0 ? (
          <div className="text-center py-16 bg-white border border-dashed border-gray-300 rounded-lg">
            <p className="font-display text-xl font-bold text-gray-800">
              {search.trim()
                ? "No matches"
                : statusTab === "history"
                ? "No completed issues yet"
                : statusTab === "active"
                ? "All caught up"
                : "No issues to show"}
            </p>
            <p className="text-sm text-gray-600 mt-1 mb-4">
              {search.trim()
                ? `Nothing matches "${search.trim()}". Try different words or clear the search.`
                : statusTab === "history"
                ? "Issues you mark complete will appear here."
                : statusTab === "active"
                ? "Nothing open right now — great work."
                : canSubmit
                ? "Submit your first maintenance issue to get started."
                : isAdmin
                ? "Add a location first, then location users can submit issues."
                : "Your account has no location assigned. Ask your admin."}
            </p>
            {canSubmit && statusTab !== "history" && (
              <Button
                className="bg-[#0055FF] hover:bg-[#0044CC]"
                data-testid="empty-new-issue"
                onClick={() => setDialogOpen(true)}
              >
                <Plus size={16} className="mr-1" /> New issue
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4" data-testid="issues-list">
            {(() => {
              // Group issues by location_id
              const byLocation = new Map();
              for (const issue of filteredIssues) {
                const arr = byLocation.get(issue.location_id) || [];
                arr.push(issue);
                byLocation.set(issue.location_id, arr);
              }

              // Render in the order of the locations array (alpha-sorted by API)
              // Only show sections that have at least one issue
              const sections = locations
                .filter((loc) => byLocation.has(loc.id))
                .map((loc) => ({ loc, items: byLocation.get(loc.id) }));

              // If a location_user has issues for an unknown loc, fall back
              for (const [lid, items] of byLocation.entries()) {
                if (!locations.find((l) => l.id === lid)) {
                  sections.push({
                    loc: { id: lid, name: items[0]?.location_name || "Unknown", address: "" },
                    items,
                  });
                }
              }

              return sections.map(({ loc, items }) => {
                const isCollapsed = collapsed.has(loc.id);
                const openCount = items.filter((i) => i.status !== "completed").length;
                return (
                  <section
                    key={loc.id}
                    data-testid={`location-section-${loc.id}`}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => toggleSection(loc.id)}
                      data-testid={`location-section-toggle-${loc.id}`}
                      className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors text-left"
                      aria-expanded={!isCollapsed}
                    >
                      <ChevronDown
                        size={18}
                        className={`text-gray-400 transition-transform ${isCollapsed ? "-rotate-90" : ""}`}
                      />
                      <div className="w-9 h-9 rounded-md bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center shrink-0">
                        <MapPin size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-bold text-lg tracking-tight truncate">
                          {loc.name}
                        </p>
                        {loc.address && (
                          <p className="text-xs text-gray-500 truncate">{loc.address}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {openCount > 0 && (
                          <span className="pill status-open" data-testid={`location-open-count-${loc.id}`}>
                            {openCount} open
                          </span>
                        )}
                        <span className="text-xs text-gray-500 tabular-nums">
                          {items.length} {items.length === 1 ? "issue" : "issues"}
                        </span>
                      </div>
                    </button>
                    {!isCollapsed && (
                      <div className="px-3 pb-3 pt-1 sm:px-4 sm:pb-4 space-y-3 border-t border-gray-100">
                        {items.map((i) => (
                          <IssueCard
                            key={i.id}
                            issue={i}
                            isAdmin={isAdmin}
                            currentUserId={user?.id}
                            onStatusChange={handleStatusChange}
                            onOpenPhoto={setPhotoPreview}
                            onDelete={isAdmin ? handleDelete : undefined}
                            onEditCost={isAdmin ? setCostIssue : undefined}
                            onEdit={setEditIssue}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                );
              });
            })()}
          </div>
        )}
      </div>

      <NewIssueDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        locations={locations}
        user={user}
        onCreated={() => load()}
      />

      <Dialog open={!!photoPreview} onOpenChange={() => setPhotoPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="photo-preview">
          <DialogHeader>
            <DialogTitle>Attached photo</DialogTitle>
          </DialogHeader>
          {photoPreview && (
            <img src={photoPreview} alt="issue" className="w-full rounded-md" />
          )}
        </DialogContent>
      </Dialog>

      <CostDialog
        open={!!costIssue}
        onOpenChange={(o) => { if (!o) setCostIssue(null); }}
        issue={costIssue}
        vendors={vendors}
        onSaved={() => { setCostIssue(null); load(); }}
      />

      <EditIssueDialog
        issue={editIssue}
        open={!!editIssue}
        onOpenChange={(o) => { if (!o) setEditIssue(null); }}
        onSaved={() => { setEditIssue(null); load(); }}
      />
    </Layout>
  );
}
